/* eslint-disable complexity */
'use strict';

const _ = require('lodash');
const moment = require('moment');
const striptags = require('striptags');

const FieldsContainer = require('./FieldsContainer');
const FieldOptions = require('./FieldOptions');

const regexFloat = /^[-]?[0-9]*\.?([0-9]+)$/;
const regexInteger = /^[-]?[0-9]+$/;

/**
 * Tipos básicos de campo según el dato que pueden contener
 *
 * @readonly
 *
 * @enum {string}
 */
const FIELD_TYPE = {
    ARRAY: 'array',
    BOOLEAN: 'boolean',
    DATE: 'date',
    DATETIME: 'datetime',
    NUMBER: 'number',
    STRING: 'string',
    TIME: 'time',
};

/**
 * Formatos de representación del campo
 *
 * @readonly
 *
 * @enum {string}
 */
const FIELD_FORMAT = {
    DATE_SHORT: 'DD/MM/YYYY',
    DATE_ISO8601: 'YYYY-MM-DD',
    DATETIME_MINUTES: 'YYYY-MM-DD HH:mm',
    DATETIME_SECONDS: 'YYYY-MM-DD HH:mm:ss',
};

/**
 * Tipos de control visual para introducir el dato en el campo
 *
 * @readonly
 *
 * @enum {string}
 */
const FIELD_FORM_CONTROL = {
    CHECKBOX: 'checkbox',
    DATEPICKER: 'datepicker',
    DATECOMPONENTS: 'datecomponents',
    DATETIMEPICKER: 'datetimepicker',
    FILES: 'files',
    DICOM: 'dicom',
    INPUT: 'input',
    NUMBER: 'number',
    RADIO: 'radio',
    RAW: 'raw',
    SELECT: 'select',
    TEXTAREA: 'textarea',
    TIME: 'time',
    VISUAL_ANALOGUE_SCALE: 'vas',
    MULTIPLE_CHECKBOX: 'multiple_checkbox',
    GRID: 'grid',
};

const vasLayout = {
    HORIZONTAL: 'horizontal',
    VERTICAL: 'vertical',
};

/**
 * Clase de manejo de campos del CRF. Es una especialización de FieldsContainer en tanto que cada campo puede tener una
 * lista de subvariables
 * @extends FieldsContainer
 * @property {object}  definition  Definición específica del campo
 * @memberOf Structure
 */
class Field extends FieldsContainer {
    /**
     * Constructor de la clase. Instancia la lista de opciones de valor si está definida
     * @param  {object} definition Definición del campo
     */
    constructor(definition = {}) {
        super(definition);

        const {
            fieldType = 'string',
            formControl = 'input',
            required,
            default: defaultValue,
            constraints,
            options,
            format,
            hidden,
            disabled,
            unknown,
            exportable,
            encrypted,
            excludedFromEpro,
            help,
            template,
            hideChildren,
            hideChildrenIfEmpty,
            hideEproLabel,
            resetDescendants,
            properties,
            partialDateSettings,
            rows,
            columns,
            cells,
            anonymize,
            ignoreDataCleaning,
            ...extra
        } = definition;

        this.fieldType = fieldType;
        this.formControl = formControl;
        this.required = !!required;
        this.default = defaultValue;
        this.constraints = constraints || {};
        this.options = this.needsOptions() ? new FieldOptions(options || {}) : null;
        this.format = format || null;
        this.hidden = !!hidden;
        this.disabled = !!disabled;
        this.unknown = !!unknown;
        this.exportable = typeof exportable === 'undefined' ? true : !!exportable;
        this.encrypted = !!encrypted;
        this.excludedFromEpro = !!excludedFromEpro;
        this.help = help || '';
        this.template = template || '';
        this.hideChildren = hideChildren || [];
        this.hideChildrenIfEmpty = !!hideChildrenIfEmpty;
        this.hideEproLabel = hideEproLabel;
        this.resetDescendants = !!resetDescendants;
        this.properties = {
            ...(this.needsLayout() ? { layout: vasLayout.HORIZONTAL } : {}),
            ...(this.getFormControl() === FIELD_FORM_CONTROL.VISUAL_ANALOGUE_SCALE ? {
                showValue: false,
                showScaleValues: false,
                showScaleMarks: false,
                showValueBox: false,
                textValueBox: '',
            } : {}),
            ...properties || {},
        };
        this.partialDateSettings = this.formControl === FIELD_FORM_CONTROL.DATECOMPONENTS ? {
            allowed: false,
            requiredDay: false,
            requiredMonth: false,
            requiredYear: false,
            ...partialDateSettings || {},
        } : null;
        this.grid = null;
        this.rows = this.getFormControl() === FIELD_FORM_CONTROL.GRID ? rows || [] : null;
        this.columns = this.getFormControl() === FIELD_FORM_CONTROL.GRID ? columns || [] : null;
        this.cells = this.getFormControl() === FIELD_FORM_CONTROL.GRID ? cells || [] : null;
        this.anonymize = this.getFormControl() === FIELD_FORM_CONTROL.DICOM ? !!anonymize : null;
        this.ignoreDataCleaning = !!ignoreDataCleaning;

        this.tags = {};
        for (const key in extra) {
            if (key.startsWith('tag:')) {
                const tagId = Number(key.replace('tag:', ''));
                this.tags[tagId] = extra[key];
            }
        }
    }

    /** @inheritdoc */
    get() {
        const definition = super.get();

        definition.fieldType = this.fieldType;
        definition.formControl = this.formControl;
        this.required && (definition.required = true);
        this.default !== null && typeof this.default !== 'undefined' && this.default !== '' && (definition.default = this.default);
        Object.keys(this.constraints).length && (definition.constraints = this.constraints);
        this.options && (definition.options = this.options.get());
        this.format && (definition.format = this.format);
        this.hidden && (definition.hidden = true);
        this.disabled && (definition.disabled = true);
        this.unknown && (definition.unknown = true);
        !this.exportable && (definition.exportable = false);
        this.encrypted && (definition.encrypted = true);
        this.excludedFromEpro && (definition.excludedFromEpro = true);
        this.help && (definition.help = this.help);
        this.template && (definition.template = this.template);
        this.hideChildren.length && (definition.hideChildren = this.hideChildren);
        this.hideChildrenIfEmpty && (definition.hideChildrenIfEmpty = true);
        this.hideEproLabel && (definition.hideEproLabel = true);
        this.resetDescendants && (definition.resetDescendants = true);
        const properties = Object.keys(this.properties).reduce((acc, key) => {
            if (this.properties[key]) {
                acc[key] = this.properties[key];
            }

            return acc;
        }, {});
        Object.keys(properties).length && (definition.properties = properties);
        this.partialDateSettings && (definition.partialDateSettings = this.partialDateSettings);
        this.rows && (definition.rows = this.rows);
        this.columns && (definition.columns = this.columns);
        this.cells && (definition.cells = this.cells);
        this.anonymize && (definition.anonymize = true);
        this.ignoreDataCleaning && (definition.ignoreDataCleaning = true);
        Object.keys(this.tags).length && (definition.tags = this.tags);

        return definition;
    }

    /**
     * Constantes de tipo de campo
     */
    static get FieldType() {
        return FIELD_TYPE;
    }

    /**
     * Constantes de tipo de control
     */
    static get FormControl() {
        return FIELD_FORM_CONTROL;
    }

    /**
     * Constantes de formato de valores
     */
    static get FieldFormats() {
        return FIELD_FORMAT;
    }

    /**
     * Devuelve TRUE
     *
     * @return {boolean} Indica que es un contenedor de tipo Field
     */
    isField() {
        return true;
    }

    /**
     * Determina si el campo se considera de obligado cumplimiento
     * @return {boolean} Valor definido, si no hay definición se devuelve falso
     */
    isRequired() {
        return this.required;
    }

    /**
     * Obtiene el formulario al que pertenece el campo. Si el campo actual es subvariable de otro campo se busca
     * el formulario del campo padre
     * @return {Form} Objeto de gestión del formulario
     */
    getForm() {
        if (this.parent instanceof Field) {
            return this.parent.getForm();
        }

        return this.parent;
    }

    /**
     * Obtiene el valor por defecto definido para el campo. Si no hay definición de valor por defecto devuelve undefined
     * @return {mixed} El valor por defecto o undefined si no hay
     */
    getDefaultValue() {
        return this.default;
    }

    /**
     * Obtiene el tipo de campo de formulario para representar el valor del campo
     *
     * @return {FIELD_FORM_CONTROL} Tipo de control de formulario
     */
    getFormControl() {
        return this.formControl;
    }

    /**
     * Obtiene el tipo de dato del campo
     *
     * @return {FIELD_TYPE} Tipo de dato
     */
    getFieldType() {
        return this.fieldType;
    }

    /**
     * Obtiene el formato de representación del campo
     *
     * @return {FIELD_FORMAT} Identificador del formato
     */
    getFormat() {
        if (!this.format) {
            this.format = this.getDefaultFormat();
        }

        return this.format;
    }
    /**
     * Devuelve un formato por defecto en función del tipo de control del campo
     * De uso en campos de fecha y fecha+hora
     *
     * @return {String|null} Formato por defecto para el campo
     *
     * @private
     */
    getDefaultFormat() {
        const formControl = this.getFormControl();

        if ([FIELD_FORM_CONTROL.DATEPICKER, FIELD_FORM_CONTROL.DATECOMPONENTS].includes(formControl)) {
            return this.getCRF().getEcrd().getConfiguration().getDateFormat();
        }

        if (formControl === FIELD_FORM_CONTROL.DATETIMEPICKER) {
            const dateFormat = this.getCRF().getEcrd().getConfiguration().getDateFormat();

            return this.getConstraint('accuracy') === 'seconds' ? `${dateFormat} HH:mm:ss` : `${dateFormat} HH:mm`;
        }

        return null;
    }

    /**
     * Obtiene la lista de restricciones del campo
     * @return {object} Objeto indexado por nombre de restricción con el valor definido
     */
    getConstraints() {
        return this.constraints;
    }

    /**
     * Obtiene una restricción concreta
     *
     * @param {string} constraint Nombre de la restricción
     *
     * @return {object} Objeto indexado por nombre de restricción con el valor definido
     */
    getConstraint(constraint) {
        return this.constraints[constraint] || null;
    }

    /**
     * Determina si el valor del campo se encuentra en una lista fija de valores
     *
     * @return {boolean} Si el tipo de representación es de radio o select, el valor está en una lista fija
     */
    needsOptions() {
        const fieldsWithOptions = [
            FIELD_FORM_CONTROL.RADIO,
            FIELD_FORM_CONTROL.SELECT,
            FIELD_FORM_CONTROL.MULTIPLE_CHECKBOX,
        ];

        return _.includes(fieldsWithOptions, this.getFormControl());
    }

    /**
     * Needs layout
     *
     * @returns {boolean} Si el campo necesita un layout específico
     */
    needsLayout() {
        return [
            FIELD_FORM_CONTROL.RADIO,
            FIELD_FORM_CONTROL.VISUAL_ANALOGUE_SCALE,
            FIELD_FORM_CONTROL.MULTIPLE_CHECKBOX,
        ].includes(this.getFormControl());
    }

    /**
     * Obtiene la lista de opciones que puede tomar el valor del campo
     *
     * @return {FieldOptions}   Definición de las opciones, null si no tiene
     */
    getOptions() {
        return this.options;
    }

    /**
     * Determina si el campo está oculto
     *
     * @return {boolean} TRUE si está oculto
     */
    isHidden() {
        return this.hidden;
    }

    /**
     * Determina si el campo está deshabilitado
     *
     * @return {boolean} TRUE si está deshabilitado
     */
    isDisabled() {
        return this.disabled;
    }

    /**
     * Determina si el campo permite valores desconocidos
     *
     * @return {boolean} TRUE si permite valores desconocidos
     */
    allowsUnknownValue() {
        const allowedTypes = [
            FIELD_FORM_CONTROL.DATEPICKER,
            FIELD_FORM_CONTROL.DATETIMEPICKER,
            FIELD_FORM_CONTROL.DATECOMPONENTS,
            FIELD_FORM_CONTROL.INPUT,
            FIELD_FORM_CONTROL.NUMBER,
            FIELD_FORM_CONTROL.TIME,
            FIELD_FORM_CONTROL.VISUAL_ANALOGUE_SCALE,
            FIELD_FORM_CONTROL.TEXTAREA,
        ];

        return allowedTypes.indexOf(this.getFormControl()) > -1 && !!this.unknown;
    }

    /**
     * Determina si el campo es exportable
     *
     * @return {boolean} TRUE si es exportable
     */
    isExportable() {
        return this.exportable;
    }

    /**
     * Determina si el campo está encriptado
     *
     * @return {boolean} TRUE si está encriptado
     */
    isEncrypted() {
        return this.encrypted;
    }

    /**
     * Determina si el campo se excluye en el ePRO
     *
     * @return {boolean} TRUE si se excluye en el ePRO
     */
    isExcludedFromEpro() {
        return this.excludedFromEpro;
    }

    /**
     * Devuelve el texto de ayuda del campo
     *
     * @return {string} Texto de ayuda
     */
    getHelp() {
        return this.help;
    }

    /**
     * Devuelve el contenido de un campo RTF
     *
     * @return {string} Contenido del campo
     */
    getTemplate() {
        return this.template;
    }

    /**
     * Devuelve el contenido de un campo RTF en formato de texto plano corto
     *
     * @param {Number} limitLength Longitud referencia para el resumen
     *
     * @return {string} Contenido del campo
     */
    getSummary(limitLength = 20) {
        const contents = striptags(this.getTemplate())
            .split(' ')
            .filter(word => !!word);
        let text = '';
        while (contents.length && text.length < limitLength) {
            text += ` ${contents.shift()}`;
        }
        contents.length && (text += '...');

        return text;
    }

    /**
     * Devuelve la cadena completa de variables padre. Es recursivo, de modo que si el padre es a su vez subvariable
     * de otra, la añade al resultado
     *
     * @return {Array<Field>} Lista de variables padre
     */
    getParentFields() {
        return this.getParents().filter(parent => parent.isField && parent.isField());
    }

    /**
     * Obtiene los valores de un selectable para las que los hijos están ocultos
     *
     * @return {string[]} Lista de valores (incluyendo NULL) para los que debe ocultarse los hijos de este campo
     */
    getValuesToHideChildren() {
        return this.hideChildren;
    }

    /**
     * Obtiene el flag de configuración del campo que indica si se quieren ocultar los hijos en caso
     * de que el valor del campo esté vacío (undefined, null o cadena vacía)
     *
     *
     * @return {Boolean} [description]
     */
    getHideChildrenIfEmpty() {
        return this.getFormControl() === FIELD_FORM_CONTROL.CHECKBOX
            ? _.includes(this.getValuesToHideChildren(), false)
            : this.hideChildrenIfEmpty;
    }

    /**
     * Devuelve la configuración del campo acerca de si deben resetearse las subvariables al ocultarse
     *
     * @return {boolean}  TRUE si deben resetearse
     */
    getResetDescendants() {
        return this.resetDescendants;
    }

    /**
     * Devuelve el valor legible de una variable. Por ejemplo, en el caso de selects, transforma el número
     * en el label del option
     *
     * @param  {*} value      Valor del campo en la BD
     *
     * @return {string}           Valor legible
     */
    getReadableValue(value) {
        let readableValue = '';
        let momentObject, format;
        let option = null;

        const type = this.getFormControl();
        switch (type) {
            case FIELD_FORM_CONTROL.RADIO:
            case FIELD_FORM_CONTROL.SELECT:
                option = this.getOptions().getOptionByValue(value);
                if (option !== null) {
                    readableValue = option.label;
                }
                break;

            case FIELD_FORM_CONTROL.DATEPICKER:
                if (typeof value === 'undefined' || value === null) {
                    readableValue = '';
                }
                else {
                    format = this.getFormat();
                    momentObject = moment(value, FIELD_FORMAT.DATE_ISO8601);
                    readableValue = momentObject.isValid() ? momentObject.format(format) : `${value}`;
                }
                break;

            case FIELD_FORM_CONTROL.CHECKBOX:
                readableValue = value ? 'Marcado' : 'Desmarcado';
                break;

            case FIELD_FORM_CONTROL.MULTIPLE_CHECKBOX:
                option = this.getOptions().getOptionByValue(Array.isArray(value) ? value : [value]) || [];
                readableValue = `${option.map(item => item.label).join(', ')}`;
                break;

            default:
                readableValue = typeof value === 'undefined' || value === null ? '' : `${value}`;
        }

        return readableValue;
    }

    /**
     * Obtiene las propiedades configuradas para la visualización del campo
     *
     * @return {object} Definición de propiedades
     */
    getProperties() {
        return this.properties;
    }

    /**
     * Determina si el campo es de valor múltiple
     *
     * @return {boolean} Si el tipo de dato es array
     */
    isMultivalued() {
        return this.getFieldType() === FIELD_TYPE.ARRAY;
    }

    /**
     * Determina dinámicamente el tipo de dato más adecuado para este campo
     * Actualmente, solo miramos en el caso de contar con opciones:
     * 1. Si todos son números enteros, devuelve 'number' y precision 0
     * 2. Si todos son números, y al menos uno es un float, devuelve 'number' y precision > 0
     * 3. Si alguno de ellos no es un número válido, devuelve 'string'
     * En todos los casos devuelve length como la longitud del valor más largo (representado como texto)
     *
     * @return {object} {type: , length: , precision: }
     */
    computeFieldType() {
        if (!this.needsOptions()) {
            return {
                type: this.getFieldType(),
            };
        }

        const types = [];
        let info;
        let length = 0, precision = 0;

        const options = this.getOptions().getOptionList();
        for (let i = 0; i < options.length; i++) { // eslint-disable-line id-length
            info = this._getOptionValueInfo(options[i].value);
            length = Math.max(length, info.length);
            precision = Math.max(precision, info.precision);
            types.push(info.type);
        }

        return {
            type: _.includes(types, 'string') ? FIELD_TYPE.STRING : FIELD_TYPE.NUMBER,
            length: length,
            precision: precision,
        };
    }

    /**
     * Devuelve información acerca del tipo de valor de una opción
     *
     * @param {string} value Valor de la opción como cadena de texto
     *
     * @return {object} { type: <'integer', 'float', 'string'>,
     *                    length: max_length como string,
     *                    precision: max_decimals en caso de float }
     */
    _getOptionValueInfo(value) {
        let match, precision = 0;
        let type = 'string';

        if (regexInteger.test(value)) {
            type = 'integer';
        }
        else if ((match = value.match(regexFloat))) {
            type = 'float';
            precision = match[1].length;
        }

        return {
            type: type,
            length: value.length,
            precision: precision,
        };
    }

    /**
     * @inheritDoc
     */
    getDataPathFromList() {
        let components = this.getPathFromList();
        if (this.isField()) {
            const parentFieldNames = this.getParentFields().map(item => item.getName());
            // quitamos los componentes padre que sean 'field'
            components = _.difference(components, parentFieldNames);
        }

        return components;
    }

    /**
     * Indica si el campo permite tener un valor (es variable), o por el contrario no puede tenerlo (es informativo)
     *
     * @return {boolean} Si el campo es de tipo variable
     */
    allowsValue() {
        return this.getFormControl() !== FIELD_FORM_CONTROL.RAW &&
            this.getFormControl() !== FIELD_FORM_CONTROL.GRID;
    }

    // Partial dates

    /**
     * Is partial date allowed
     *
     * @returns {Boolean} TRUE if partial date is allowed
     */
    isPartialDateAllowed() {
        return this.formControl === FIELD_FORM_CONTROL.DATECOMPONENTS ? this.partialDateSettings.allowed : false;
    }
    /**
     * Is day required in partial date
     *
     * @returns {Boolean} TRUE if day required in partial date
     */
    isDayRequiredInPartialDate() {
        return this.formControl === FIELD_FORM_CONTROL.DATECOMPONENTS ? this.partialDateSettings.requiredDay : false;
    }
    /**
     * Is month required in partial date
     *
     * @returns {Boolean} TRUE if month required in partial date
     */
    isMonthRequiredInPartialDate() {
        return this.formControl === FIELD_FORM_CONTROL.DATECOMPONENTS ? this.partialDateSettings.requiredMonth : false;
    }
    /**
     * Is year required in partial date
     *
     * @returns {Boolean} TRUE if year required in partial date
     */
    isYearRequiredInPartialDate() {
        return this.formControl === FIELD_FORM_CONTROL.DATECOMPONENTS ? this.partialDateSettings.requiredYear : false;
    }

    // Grid

    /**
     * @inheritDoc
     *
     * Si el campo es de tipo grid recorre la matriz de subvariables
     */
    getChildren() {
        if (this.getFormControl() === FIELD_FORM_CONTROL.GRID) {
            return this.getCells().reduce((acc, cellsRow) => {
                return acc.concat(cellsRow.filter(cellColumn => cellColumn !== null));
            }, []);
        }

        return super.getChildren();
    }

    /**
     * Establece el grid como contenedor que incluye al campo actual
     *
     * @param  {Structure.Field} grid Instancia del grid padre
     *
     * @return {Structure.Field}      Instancia del campo perteneciente al grid
     */
    setGrid(grid) {
        this.grid = grid;

        return this;
    }

    /**
     * Obtiene el grid que incluye al campo actual
     *
     * @return {Structure.Field} Instancia del grid padre
     */
    getGrid() {
        return this.grid;
    }

    /**
     * Obtiene la definición de filas del grid actual
     *
     * @return {string[]} Lista de cabeceras de fila
     */
    getRows() {
        return this.rows;
    }

    /**
     * Obtiene la definición de columnas del grid actual
     *
     * @return {string[]} Lista de cabeceras de columna
     */
    getColumns() {
        return this.columns;
    }

    /**
     * Establece la matriz de campos del grid
     *
     * @param  {Array<(Structure.Field|null)[]>} cells Lista de campos que pertenecen al grid
     *
     * @return {Structure.Field}                       Instancia del grid
     */
    setCells(cells) {
        this.cells = cells;

        return this;
    }

    /**
     * Obtiene la matriz de campos del grid
     *
     * @return {Array<(Structure.Field|null)[]>} Lista de campos que pertenecen al grid
     */
    getCells() {
        return this.cells;
    }

    /**
     * Si el campo pertenece a un grid, obtiene las coordenadas dentro de la cuadrícula donde se encuentra
     *
     * @return {integer[]} Coordenadas [fila, columna] del campo dentro del grid. Undefined si no pertenece a un grid
     */
    getGridPosition() {
        const grid = this.getGrid();
        if (!grid || grid.getFormControl() !== FIELD_FORM_CONTROL.GRID) {
            return undefined;
        }

        const cells = grid.getCells();

        for (let rowIndex = 0; rowIndex < cells.length; rowIndex++) {
            for (let colIndex = 0; colIndex < cells[rowIndex].length; colIndex++) {
                if (cells[rowIndex][colIndex] === this) {
                    return [rowIndex, colIndex];
                }
            }
        }
    }

    /**
     * Si el campo pertenece a un grid, devuelve el título de la fila que lo contiene
     *
     * @return {string} Título de la fila donde se encuentra el campo
     */
    getGridRowName() {
        const position = this.getGridPosition();

        // Si position es un array sabemos que pertenece a un grid
        return Array.isArray(position) ? this.getGrid().getRows()[position[0]] : undefined;
    }

    /**
     * Si el campo pertenece a un grid, devuelve el título de la fila que lo contiene
     *
     * @return {string} Título de la fila donde se encuentra el campo
     */
    getGridColumnName() {
        const position = this.getGridPosition();

        // Si position es un array sabemos que pertenece a un grid
        return Array.isArray(position) ? this.getGrid().getColumns()[position[1]] : undefined;
    }

    // DICOM

    /**
     * Si el estudio DICOM adjunto se debe anonimizar antes de subir
     *
     * @returns {Boolean} Si el estudio DICOM adjunto se debe anonimizar antes de subir
     */
    getAnonymize() {
        return this.anonymize;
    }

    // VAS

    /**
     * Get layout
     *
     * @returns {string} Layout del campo VAS
     */
    getLayout() {
        return this.properties.layout;
    }

    /**
     * Get show scale values
     *
     * @returns {boolean} Si se debe mostrar el valor del campo
     */
    getShowScaleValues() {
        return this.properties.showScaleValues;
    }

    /**
     * Get show scale marks
     *
     * @returns {boolean} Si se deben mostrar las marcas de la escala
     */
    getShowScaleMarks() {
        return this.properties.showScaleMarks;
    }

    /**
     * Get show value box
     *
     * @returns {boolean} Si se debe mostrar el cuadro con el valor del campo
     */
    getShowValueBox() {
        return this.properties.showValueBox;
    }

    /**
     * Get value box text
     *
     * @returns {string} Texto mostradoe en el cuadro con el valor del campo
     */
    getTextValueBox() {
        return this.properties.textValueBox;
    }

    // General

    /**
     * @inheritDoc
     *
     * Si el campo es de tipo grid recorre los elementos definidos en la cuadrícula
     */
    getAllElements() {
        if (this.getFormControl() === FIELD_FORM_CONTROL.GRID) {
            return this.getCells().reduce((gridAcc, row) => {
                return gridAcc.concat(row.reduce((rowAcc, cell) => {
                    return cell ? rowAcc.concat(cell) : rowAcc;
                }, []));
            }, []);
        }

        return super.getAllElements();
    }

    /**
     * Si no se debe tener en cuenta el campo en un data-cleaning
     *
     * @returns {Boolean} Si no se debe tener en cuenta el campo en un data-cleaning
     */
    ignoresDataCleaning() {
        return this.ignoreDataCleaning;
    }

    /**
     * Has tag
     *
     * @param {number} tagId Tag ID
     *
     * @returns {boolean} If the field has the tag
     */
    hasTag(tagId) {
        return this.tags[tagId];
    }
}

module.exports = Field;
